import { generatePath } from "react-router";

import { Claim } from "shared/api/claims/api";
import { EventTypeEnum } from "shared/types";
import { randomID } from "shared/utils";

import {
  EVENT_TYPE_LABEL,
  LANDING_PAGE_TAB_KEY,
} from "pages/shared/Dashboard/constants";
import { convertDateDropdownOptionToDateRangeFilter } from "pages/shared/Dashboard/DateFieldDropdown/utils";
import {
  LandingPageChartConfig,
  RelatedEventGroup,
} from "pages/shared/Dashboard/types";

import { SelectedChartOptions } from "features/ui/charts/Actions/types";
import { FILTER_OPERATOR_TO_API_FILTER } from "features/ui/Filters/constants";
import {
  FilterGroupState,
  FilterRowState,
} from "features/ui/Filters/FilterBuilder/types";
import { mergeFilterGroupStates } from "features/ui/Filters/FilterBuilder/utils";
import { AdvancedFilterConfig } from "features/ui/Filters/StaticFilters/constants";
import { FilterOperator } from "features/ui/Filters/types";
import { quickFiltersToFilters } from "features/ui/Filters/utils";
import { SchemaEntry } from "features/ui/MaterialTable";

import { routes } from "services/routes";

import { LandingPageEventDetailsConfig, LandingPageViewConfig } from "./types";

export const getDefaultLandingPageViewConfig = (
  eventType: EventTypeEnum = EventTypeEnum.CLAIM,
  previousConfig?: LandingPageViewConfig
): LandingPageViewConfig => {
  const chart = getDefaultChartConfig([]);
  const title = EVENT_TYPE_LABEL[eventType] ?? "Untitled";

  return {
    versionKey: randomID(),
    chartSettings: chart,
    title,
    dateField: getDefaultDateField(eventType),
    vehicleDateField: "vehicleManufacturedAt",
    vehicleDateFieldOptions: ["3d", "1w", "2w", "1m", "3m", "6m", "1y"],
    eventType,
    dateFieldOptions: ["3d", "1w", "2w", "1m", "3m", "6m", "1y"],
    dateFieldDropdownOptions: previousConfig?.dateFieldOptions?.map(
      (option) => ({ type: "inlast", value: option })
    ) ?? [{ type: "inlast", value: "1w" }],
    columnAccessors: [],
    eventDetails: getDefaultDetailsConfig(eventType),
    investigator: {
      topContributorPinOptions: [],
    },
  };
};

const getDefaultDateField = (eventType: EventTypeEnum) => {
  switch (eventType) {
    case EventTypeEnum.CLAIM:
      return "date";
    case EventTypeEnum.SIGNAL_EVENT:
      return "recordedAt";
    default:
      return "createdAt";
  }
};

export const getDefaultChartConfig = (
  selectedOptions: SelectedChartOptions[]
): LandingPageChartConfig => ({
  exposureTitle: "Vehicle age",
  exposureBuckets: ["0", "1", "2", "3"],
  mainExposureBucket: "3",
  exposureUnit: "MIS",
  exposureUnitLong: "Months in service",
  selectedChartOptions: selectedOptions,
  chartDateField: "vehicleManufacturedAt",
});

const DEFAULT_MAIN_FIELDS: (keyof Claim)[] = ["group", "subgroup"];

const DEFAULT_COMMENT_FIELDS: (keyof Claim)[][] = [
  ["notesTechnician"],
  ["notesCustomer", "binComponent"],
];

const DEFAULT_VEHICLE_FIELDS: string[][] = [
  ["vehicle.vehicleManufacturedAt", "mileage", "monthsInService"],
  ["vehicle.vehicleModel", "vehicle.trimLevel", "vehicle.body"],
];

const DEFAULT_COST_FIELDS: (keyof Claim)[][] = [
  ["costParts", "costLabor", "costOther", "costTotal"],
];

export const DEFAULT_SIGNAL_EVENT_GROUPS: RelatedEventGroup[] = [
  {
    eventType: EventTypeEnum.SIGNAL_EVENT,
    signalEventType: "",
    title: "Related QLS concerns",
    comparisonFields: ["notesTechnician", "notesCustomer"],
    titleField: "signalEventID",
    dateField: "recordedAt",
    fields: [
      ["repairFirstName", "repairLastName", "crewRepair"],
      ["evaluationComment"],
      ["repairComment"],
    ],
  },
];

export const DEFAULT_CLAIM_GROUPS: RelatedEventGroup[] = [
  {
    eventType: EventTypeEnum.CLAIM,
    title: "Related claims",
    comparisonFields: ["notesTechnician", "notesCustomer"],
    titleField: "externalID",
    dateField: "date",
    fields: [["notesTechnician", "notesCustomer"], ["binComponent"]],
  },
];

export const getDefaultDetailsConfig = (
  eventType: EventTypeEnum
): LandingPageEventDetailsConfig =>
  eventType === EventTypeEnum.CLAIM
    ? {
        titleField: "group",
        mainFields: DEFAULT_MAIN_FIELDS,
        cardRows: [
          [
            {
              type: "vertical",
              title: "Comments",
              containerClass: "flex-1",
              fields: DEFAULT_COMMENT_FIELDS,
            },
          ],
          [
            {
              type: "horizontal",
              title: "Vehicle info",
              containerClass: "flex-2",
              fields: DEFAULT_VEHICLE_FIELDS,
            },
            {
              type: "horizontal",
              title: "Cost",
              containerClass: "flex-1",
              fields: DEFAULT_COST_FIELDS,
            },
          ],
        ],
        relatedEvents: DEFAULT_SIGNAL_EVENT_GROUPS,
      }
    : {
        titleField: "signalEventID",
        mainFields: [],
        cardRows: [],
        relatedEvents: [],
      };

export const getLandingPageViewKey = (config: LandingPageViewConfig) =>
  LANDING_PAGE_TAB_KEY(
    config,
    `${config.eventType}_${config.title.toLowerCase().replace(/\s+/g, "_")}`
  );

export const getVINViewLinkParams = (
  config: LandingPageViewConfig,
  vin: string
) => {
  const tabKey = config.addToVINView
    ? getLandingPageViewKey(config)
    : undefined;
  const linkSearchParams = tabKey ? `?tab=${tabKey}` : undefined;

  return {
    pathname: generatePath(routes.vinView, { vin }),
    search: linkSearchParams,
  };
};

export const getFullEventDefaultFilters = (config: LandingPageViewConfig) => {
  const filters = quickFiltersToFilters(config.defaultEventsFilter ?? []);

  if (config.enableEventDateDropdown === false) {
    return filters;
  }

  const dateFilter = convertDateDropdownOptionToDateRangeFilter(
    config.defaultEventDateOption ?? config.dateFieldDropdownOptions[0] ?? "1w",
    config.dateField,
    false
  );

  return mergeFilterGroupStates(filters, quickFiltersToFilters([dateFilter]));
};

export const getFullVehicleDefaultFilters = (config: LandingPageViewConfig) => {
  const filters = quickFiltersToFilters(config.defaultVehiclesFilter ?? []);

  if (config.enableVehicleDateDropdown === false) {
    return filters;
  }

  const dateFilter: FilterRowState = {
    type: "row",
    id: `row-${randomID()}`,
    attribute: config.vehicleDateField,
    operator: FilterOperator.IN_LAST,
    values: [
      config.defaultVehicleDateValue ??
        config.vehicleDateFieldOptions[0] ??
        "4m",
    ],
  };

  return mergeFilterGroupStates(filters, quickFiltersToFilters([dateFilter]));
};

export const advancedFiltersToFilters = (
  advancedFilters: AdvancedFilterConfig[],
  childSchemaMap?: Record<string, SchemaEntry[]>
): FilterGroupState => {
  const filterRows: FilterRowState[] = advancedFilters
    .filter(({ childFilters }) => childFilters.length > 0)
    .map(
      ({
        childFilters,
        id,
        parentAttribute,
        parentOperator,
        relationEndpoint,
        childDateDefaultOptions,
        childDateSelectedOptions,
      }) => {
        const childSchema = childSchemaMap?.[relationEndpoint] || [];

        const childFiltersQuery = childFilters
          .map((childFilter) => {
            const { attribute, operator, values } = childFilter;

            const dateOption =
              childDateSelectedOptions?.[attribute!] ||
              childDateDefaultOptions?.[attribute!];

            let op: FilterOperator | null = operator;
            let vals: string[] | null = values;

            // todo mbencina: instead of includes("time") we should check advanced date filter setting
            // if we have a date option, dynamically evaluate it
            if (dateOption && childSchemaMap) {
              const schema = childSchema.find((s) => s.accessor === attribute);
              const isDateTime =
                schema?.filter?.filterDataType
                  ?.toLowerCase()
                  .includes("time") ?? false;

              const filterRow = convertDateDropdownOptionToDateRangeFilter(
                dateOption,
                attribute!,
                isDateTime
              );

              op = filterRow.operator;
              vals = filterRow.values;
            }

            // skip filters that don't have operator and values
            if (!attribute || !op || !vals || vals.length === 0) {
              return null;
            }

            const apiOperator = FILTER_OPERATOR_TO_API_FILTER[op];

            let valueStr = "";
            if (op === FilterOperator.IN || op === FilterOperator.NOT_IN) {
              valueStr = vals.map((v) => `"${v}"`).join("|");
            } else if (op === FilterOperator.IN_LAST) {
              valueStr = vals[0] || "";
            } else if (op === FilterOperator.BETWEEN) {
              valueStr = vals.join("|");
            } else {
              valueStr = vals.join(",");
            }

            return `${attribute}=${apiOperator}:${valueStr}`;
          })
          .filter(Boolean)
          .join(",");

        return {
          type: "row" as const,
          id,
          attribute: parentAttribute,
          operator: parentOperator,
          values: childFiltersQuery ? [`[${childFiltersQuery}]`] : [],
        };
      }
    );

  return {
    type: "group",
    id: randomID(),
    anyAll: "all",
    children: filterRows,
  };
};
